import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { HeaderComponent } from './shared/components/header/header.component';
import { FooterComponent } from './shared/components/footer/footer.component';
import { MainLayoutComponent } from './shared/components/layouts/main-layout/main-layout.component';
import { AppMaterialModule } from './app-material.module';
import { environment } from 'src/environments/environment';
import { NgxsFormPluginModule } from '@ngxs/form-plugin';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsRouterPluginModule } from '@ngxs/router-plugin';
import { NgxsLoggerPluginModule } from '@ngxs/logger-plugin';
import { NgxsResetPluginModule } from 'ngxs-reset-plugin';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgxsModule } from '@ngxs/store';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { registerLocaleData } from '@angular/common';
import localeEs from '@angular/common/locales/es';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { InternationalizationLanguagesModule, LanguagesSelectorModule, } from './shared/components/languages-selector/languages-selector.module';
import { ReportState } from './modules/reporting/store/report/report.state';
import { LanguageState } from './modules/reporting/store/language/language.state';
import { LanguageTypeState } from './shared/store/language-type/language-type.state';
import { AttendanceStatusState } from './modules/attendance/store/attendance-status/attendance-status.state';
import { ToastrModule } from 'ngx-toastr';
import { MassiveActionsState } from './modules/massive-actions/store/massive/massive.state';
import { SignalRState } from './modules/massive-actions/store/signalr/signalr.state';
import { AttendanceState } from './modules/attendance/store/attendance/attendance.state';
import { FamilyState } from './modules/family/store/family/family.state';
import { IdentificationTypeState } from './shared/store/identification-type/identification-type.state';
import { DebtState } from './shared/store/debt/debt.state';
import { SchoolState } from './modules/school/store/school.state';
import { EventState } from './modules/event/store/event.state';
import { ExerciseState } from './modules/exercise/store/exercise.state';
import { ZoneState } from './modules/zone/store/zone.state';
import { CenterState } from './modules/center/store/center.state';
import { StudentState } from './modules/student/store/student.state';
import { CourseState } from './modules/course/store/course.state';
import { ServiceTypeState } from './modules/service/store/service-type.state';
import { ScholarshipTypeState } from './modules/scholarship/store/scholarship-type/scholarship-type.state';
import { ScholarshipState } from './modules/scholarship/store/scholarship.state';
import { AttendanceContractTypeState } from './modules/attendance/store/attendance-contract-type/attendance-contract-type.state';
import { EngagementState } from './modules/engagement/store/engagement.state';
import { HolidaysState } from './modules/holidays/store/holidays.state';
import { SchoolServiceState } from './modules/school-service/store/school-service.state';
import { ServiceConceptState } from './modules/service-concept/store/service-concept/service-concept.state';
import { ServiceConceptTypeState } from './modules/service-concept/store/service-concept-type/service-concepts-type.state';
import { ContractState } from './modules/contract/stores/contract/contract.state';
import { AttendanceServicesState } from './modules/attendance-services/stores/attendance-services.state';
import { ManagerState } from './modules/manager/store/manager.state';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { MatPaginationIntlService } from './shared/services/mat-pagination-intl-service';
import { DietState } from './modules/diets/store/diet.state';
import { AuthState } from './modules/login/store/auth.state';
import { PublicLayoutComponent } from './shared/components/layouts/public-layout/public-layout.component';
import { ProfileMenuComponent } from './shared/components/profile-menu/profile-menu.component';
import { AuthInterceptor } from './core/interceptors/auth-interceptor';
import { MonitorState } from './modules/monitor/store/monitor.state';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';
import { InvoiceTemplateState } from './modules/invoice-template/store/invoice-template.state';
import { TaskState } from './modules/task/store/task.state';
import { MonthState } from './shared/store/month/month.state';
import { CycleState } from './modules/cycle/store/cycle.state';
import { PaymentMethodState } from './shared/store/payment-method/payment-method.state';
import { PaymentTermState } from './shared/store/payment-term/payment-term.state';
import { MenuItemComponent } from './shared/components/menu-item/menu-item.component';
import { PermissionState } from './modules/permissions/store/permision.state';
import { ImportsState } from './modules/imports/store/imports.state';
import { TaskStatusState } from './shared/store/task-status/task-status.state';
import { MessageStatusState } from './shared/store/message-status/message-status.state';
import { ContractTypeState } from './shared/store/contract-type/contract-type.state';
import { VersionService } from './shared/services/version.service';
import { CustomTranslateLoader } from './services/localization/custom-translate-loader';
import { MessageState } from './shared/store/message/message.state';
import { MainLayoutMonitorComponent } from './shared/components/layouts/main-layout-monitor/main-layout-monitor.component';
import { HeaderMonitorComponent } from './shared/components/header-monitor/header-monitor.component';
import { PublicLayoutMonitorComponent } from './shared/components/layouts/public-layout-monitor/public-layout-monitor.component';
import { NonProductiveEnvironmentComponent } from './shared/components/non-productive-environment/non-productive-environment.component';
import { CountryState } from './shared/store/country/country.state';
import { GenderState } from './shared/store/gender/gender.state';
import { OperatingMonthState } from './modules/operating-month/store/operating-month/operating-month.state';

registerLocaleData(localeEs, 'es');

export function HttpLoaderFactory(http: HttpClient, versionService: VersionService) {
  return new CustomTranslateLoader(http, versionService);
}

@NgModule({
  declarations: [
    AppComponent,
    HeaderComponent,
    HeaderMonitorComponent,
    FooterComponent,
    MainLayoutComponent,
    MainLayoutMonitorComponent,
    ProfileMenuComponent,
    NonProductiveEnvironmentComponent,
    PublicLayoutComponent,
    PublicLayoutMonitorComponent,
    MenuItemComponent
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppMaterialModule,
    HttpClientModule,
    InternationalizationLanguagesModule,
    NgxExtendedPdfViewerModule,
    LanguagesSelectorModule.forRoot({ locale_id: 'es' }),
    NgxsModule.forRoot([
      AuthState,
      AttendanceState,
      AttendanceContractTypeState,
      AttendanceServicesState,
      AttendanceStatusState,
      ServiceTypeState,
      CenterState,
      ContractState,
      ContractTypeState,
      CountryState,
      CourseState,
      CycleState,
      DebtState,
      EventState,
      ExerciseState,
      EngagementState,
      FamilyState,
      GenderState,
      HolidaysState,
      IdentificationTypeState,
      LanguageState,
      LanguageTypeState,
      ManagerState,
      MassiveActionsState,
      MessageState,
      MessageStatusState,
      MonthState,
      OperatingMonthState,
      PaymentMethodState,
      PaymentTermState,
      ReportState,
      SchoolState,
      SchoolServiceState,
      ServiceConceptState,
      ServiceConceptTypeState,
      ScholarshipTypeState,
      ScholarshipState,
      SignalRState,
      StudentState,
      TaskState,
      TaskStatusState,
      ZoneState,
      DietState,
      MonitorState,
      ImportsState,
      InvoiceTemplateState,
      PermissionState
    ],
    { developmentMode: !environment.production }
    ),
    NgxsFormPluginModule.forRoot(),
    NgxsReduxDevtoolsPluginModule.forRoot({
      disabled: environment.production,
    }),
    NgxsRouterPluginModule.forRoot(),
    NgxsLoggerPluginModule.forRoot({
      disabled: environment.production,
      collapsed: true,
    }),
    NgxsResetPluginModule.forRoot(),
    FormsModule,
    ReactiveFormsModule,
    ToastrModule.forRoot(),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient, VersionService],
      },
    }),
    AppRoutingModule,
  ],
  providers: [
    {
      provide: MatPaginatorIntl,
      useClass: MatPaginationIntlService,
    },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
